// Персистентный кэш разбора в localStorage по id партии.
// Сериализует cp-оценки главной линии + артефакты движка на узлах
// (bestmove/pv/depth/bestCp/secondCp/mateWhite/brilliant). LRU на 20 партий,
// try/catch на квоту. Повторное открытие → мгновенно; прерванный разбор
// продолжается: reviewGame сам пропускает узлы с валидными артефактами.
import type { GameNode } from '@ext/core/types';
import type { EvalStore } from '@ext/core/evals';
import { nodeFen } from '@ext/core/evals';
import { mainLineOf } from '@ext/core/tree';

const KEY_PREFIX = 'cc_eval_';
const LRU_KEY = 'cc_eval_lru';
const LRU_MAX = 20;
const VERSION = 1;

interface NodeArtifact {
  c: number | null; // cp со стороны белых (evals)
  pv?: string[];
  bm?: string | null;
  d?: number | null;
  bc?: number | null; // _bestCp
  sc?: number | null; // _secondCp
  mw?: number | null; // _mateWhite
  br?: boolean; // _brilliant
}

interface Payload {
  v: number;
  complete: boolean;
  nodes: (NodeArtifact | null)[];
}

function store(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

/** [root, …главная линия] — узлы, на которых живут оценки и артефакты. */
function mainNodes(root: GameNode): GameNode[] {
  return [root, ...mainLineOf(root)];
}

function serializeNode(n: GameNode, evals: EvalStore): NodeArtifact | null {
  const c = evals.getNode(n);
  const hasArtifacts = n._pv != null || n._bestmove != null || n._depth != null;
  if (c == null && !hasArtifacts) return null;
  const a: NodeArtifact = { c };
  if (n._pv) a.pv = n._pv;
  if (n._bestmove !== undefined) a.bm = n._bestmove;
  if (n._depth != null) a.d = n._depth;
  if (n._bestCp !== undefined) a.bc = n._bestCp;
  if (n._secondCp !== undefined) a.sc = n._secondCp;
  if (n._mateWhite !== undefined) a.mw = n._mateWhite;
  if (n._brilliant) a.br = true;
  return a;
}

function restoreNode(n: GameNode, a: NodeArtifact, evals: EvalStore): void {
  if (a.c != null) evals.set(nodeFen(n), a.c);
  if (a.pv) n._pv = a.pv;
  if (a.bm !== undefined) n._bestmove = a.bm;
  if (a.d != null) n._depth = a.d;
  if (a.bc !== undefined) n._bestCp = a.bc;
  if (a.sc !== undefined) n._secondCp = a.sc;
  if (a.mw !== undefined) n._mateWhite = a.mw;
  if (a.br) n._brilliant = true;
}

/** Сохранить разбор партии. complete=true → разбор дошёл до конца. */
export function saveEvals(gameId: string, root: GameNode, evals: EvalStore, complete = false): void {
  const ls = store();
  if (!ls) return;
  const nodes = mainNodes(root).map((n) => serializeNode(n, evals));
  const payload: Payload = { v: VERSION, complete, nodes };
  const json = JSON.stringify(payload);
  if (!writeWithQuota(ls, KEY_PREFIX + gameId, json)) return;
  touchLru(ls, gameId);
}

/** Восстановить разбор в evals + на узлы. Возвращает найден/завершён. */
export function loadEvals(gameId: string, root: GameNode, evals: EvalStore): { found: boolean; complete: boolean } {
  const ls = store();
  if (!ls) return { found: false, complete: false };
  let payload: Payload;
  try {
    const raw = ls.getItem(KEY_PREFIX + gameId);
    if (!raw) return { found: false, complete: false };
    payload = JSON.parse(raw) as Payload;
  } catch {
    return { found: false, complete: false };
  }
  if (payload.v !== VERSION || !Array.isArray(payload.nodes)) return { found: false, complete: false };
  const nodes = mainNodes(root);
  const len = Math.min(nodes.length, payload.nodes.length);
  for (let i = 0; i < len; i++) {
    const a = payload.nodes[i];
    if (a) restoreNode(nodes[i]!, a, evals);
  }
  touchLru(ls, gameId);
  return { found: true, complete: Boolean(payload.complete) };
}

function readLru(ls: Storage): string[] {
  try {
    const raw = ls.getItem(LRU_KEY);
    const arr = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(arr) ? arr.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

/** id → в конец списка (самый свежий), лишние сверх LRU_MAX выселяем с payload. */
function touchLru(ls: Storage, gameId: string): void {
  const list = readLru(ls).filter((id) => id !== gameId);
  list.push(gameId);
  while (list.length > LRU_MAX) {
    const evicted = list.shift();
    if (evicted) {
      try {
        ls.removeItem(KEY_PREFIX + evicted);
      } catch {
        /* ignore */
      }
    }
  }
  try {
    ls.setItem(LRU_KEY, JSON.stringify(list));
  } catch {
    /* ignore */
  }
}

/** Запись с одной попыткой освободить место при переполнении квоты. */
function writeWithQuota(ls: Storage, key: string, value: string): boolean {
  try {
    ls.setItem(key, value);
    return true;
  } catch {
    const list = readLru(ls);
    const oldest = list[0];
    if (oldest) {
      try {
        ls.removeItem(KEY_PREFIX + oldest);
        ls.setItem(LRU_KEY, JSON.stringify(list.slice(1)));
      } catch {
        /* ignore */
      }
    }
    try {
      ls.setItem(key, value);
      return true;
    } catch {
      return false;
    }
  }
}
